import { useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Mail, Pencil, Trash2, UserPlus } from 'lucide-react';
import { useUsuarios, useExcluirUsuario } from '@/services/Configuracoes';
import type { UsuarioGestorApi } from '@/services/Configuracoes';
import { useReenviarConvite } from '@/services/Auth';
import { NovoUsuarioDialog } from './NovoUsuarioDialog';
import { EditarUsuarioDialog } from './EditarUsuarioDialog';

const perfilConfig: Record<string, { label: string; className: string }> = {
  Admin: {
    label: 'Administrador',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100',
  },
  Contador: {
    label: 'Contador',
    className: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100',
  },
  Auxiliar: {
    label: 'Auxiliar',
    className: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100',
  },
  Visualizador: {
    label: 'Visualizador',
    className: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100',
  },
};

export function GestaoUsuariosTab() {
  const { data: response, isLoading } = useUsuarios();
  const usuarios = response?.data ?? [];
  const [novoUsuarioOpen, setNovoUsuarioOpen] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState<UsuarioGestorApi | null>(null);
  const [usuarioExcluindo, setUsuarioExcluindo] = useState<UsuarioGestorApi | null>(null);
  const excluirMutation = useExcluirUsuario();
  const reenviarConviteMutation = useReenviarConvite();

  const handleConfirmarExclusao = () => {
    if (!usuarioExcluindo) return;
    excluirMutation.mutate(usuarioExcluindo.Email, {
      onSuccess: response => response.status.resultado && setUsuarioExcluindo(null),
    });
  };

  return (
    <TabsContent value="usuarios" className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-base">Usuários do Sistema</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Gerencie os usuários e suas permissões
            </p>
          </div>
          <Button size="sm" className="gap-2" onClick={() => setNovoUsuarioOpen(true)}>
            <UserPlus className="h-4 w-4" />
            Novo Usuário
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead className="w-36">Perfil</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-40">Último Acesso</TableHead>
                  <TableHead className="w-20 text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  usuarios.map(usuario => {
                    const config = perfilConfig[usuario.Perfil] ?? perfilConfig['Visualizador'];
                    return (
                      <TableRow key={usuario.Email}>
                        <TableCell className="font-medium">{usuario.Nome}</TableCell>
                        <TableCell className="text-muted-foreground">{usuario.Email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={config.className}>
                            {config.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              usuario.Status === 'A' || usuario.Status === 'ativo'
                                ? 'bg-green-500 text-white hover:bg-green-500'
                                : usuario.Status === 'P'
                                  ? 'bg-amber-500 text-white hover:bg-amber-500'
                                  : 'bg-gray-400 text-white hover:bg-gray-400'
                            }
                          >
                            {usuario.Status === 'A'
                              ? 'ativo'
                              : usuario.Status === 'I'
                                ? 'inativo'
                                : usuario.Status === 'P'
                                  ? 'convite pendente'
                                  : usuario.Status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {usuario.UltimoAcesso || '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            {usuario.Status === 'P' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() =>
                                  reenviarConviteMutation.mutate({ EmailLogin: usuario.Email })
                                }
                                disabled={reenviarConviteMutation.isPending}
                                aria-label="Reenviar convite"
                                title="Reenviar convite"
                              >
                                <Mail className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setUsuarioEditando(usuario)}
                              aria-label="Editar usuário"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setUsuarioExcluindo(usuario)}
                              aria-label="Excluir usuário"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NovoUsuarioDialog open={novoUsuarioOpen} onOpenChange={setNovoUsuarioOpen} />

      <EditarUsuarioDialog
        usuario={usuarioEditando}
        onOpenChange={open => !open && setUsuarioEditando(null)}
      />

      <AlertDialog
        open={!!usuarioExcluindo}
        onOpenChange={open => !open && setUsuarioExcluindo(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Remove o acesso de <strong>{usuarioExcluindo?.Nome}</strong> a esta empresa. Deseja
              continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={excluirMutation.isPending}
              onClick={() => setUsuarioExcluindo(null)}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarExclusao}
              disabled={excluirMutation.isPending}
            >
              {excluirMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabsContent>
  );
}
